import { getConfig, getTranslation } from '../../utils/config';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import humanFile from '../../utils/human-file';
import Share from '../share';
import { useDebouncedCallback } from 'use-debounce';
import type { IBookmarkGroup, IItem } from '../../types';

type BookmarkGroupViewProps = {
  isExpanded: boolean;
  bookmarkGroup: IBookmarkGroup;
  toggleExpanded: (toggle?: boolean) => void;
  groupAttributes: Array<{ pimId: number; displayname: string; facetname: string }>;
  handleSelection: (pimId: number) => void;
  loginRequired: boolean;
  selectedBookmarks: Array<number>;
  getDownloadlink: (file: IItem) => string;
  handleDownload: (e: React.MouseEvent) => void;
  groupId: number;
  removeItem: (item: number) => void;
};

export function BookmarkGroupView({
  isExpanded,
  bookmarkGroup,
  toggleExpanded,
  groupAttributes,
  loginRequired,
  getDownloadlink,
  handleDownload,
  groupId,
  removeItem,
}: BookmarkGroupViewProps): React.ReactElement {
  const [clampText, setClampText] = useState(!!bookmarkGroup?.text);

  const textRef = useRef<HTMLDivElement>(null);

  const handleResize = useDebouncedCallback(() => {
    if (textRef.current) {
      setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
    }
  }, 300);

  const handleTextClamp = useCallback(() => {
    // close more info text before checking offsetHeight
    toggleExpanded(true);
    handleResize();
  }, [handleResize, toggleExpanded]);

  useEffect(() => {
    if (textRef.current) {
      setClampText(textRef.current.offsetHeight < textRef.current.scrollHeight);
    }
    window.addEventListener('resize', handleTextClamp);
    return () => {
      window.removeEventListener('resize', handleTextClamp);
    };
  }, [handleTextClamp, textRef]);

  return (
    <>
      {loginRequired && (
        <span className="df-list-module__login-required">{getTranslation('login_required')}</span>
      )}
      {bookmarkGroup.dfSubtitle && (
        <span
          className="df-list-module__note"
          dangerouslySetInnerHTML={{ __html: bookmarkGroup.dfSubtitle }}
        />
      )}
      {bookmarkGroup?.text && (
        <div className="df-show-hide-details">
          <div
            className={`df-text df-teaser-text ${isExpanded ? '' : 'df-teaser-text--minimized'}`}
            dangerouslySetInnerHTML={{ __html: bookmarkGroup?.text || '' }}
            ref={textRef}
          />
          {clampText && (
            <button
              className="df-show-hide-details__button df-show-hide-details__button--chevron collapsed"
              aria-expanded={isExpanded}
              type="button"
              onClick={() => toggleExpanded(isExpanded)}
            >
              {getTranslation(isExpanded ? 'less_info' : 'more_info')}
            </button>
          )}
        </div>
      )}
      <table className="df-list-module__table df-download-table-bookmark">
        <thead>
          <tr>
            <th></th>
            {groupAttributes?.map((attribute) => (
              <th key={attribute.facetname} className="df-download-table-bookmark--attribute">
                {attribute.displayname}
              </th>
            ))}
            <th> </th>
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {bookmarkGroup?.items?.map((file: IItem) => (
            <tr key={file.pimId}>
              <td>&nbsp;</td>
              {groupAttributes?.map((attribute) => (
                <td key={attribute.facetname}>
                  {Array.isArray(file[attribute.facetname])
                    ? (file[attribute.facetname] as Array<string>).join(', ')
                    : file[attribute.facetname]}
                </td>
              ))}
              <td>
                <a
                  href={getDownloadlink(file)}
                  onClick={handleDownload}
                  className={`df-download-link  ${
                    loginRequired ? 'df-download-link__login-required' : ''
                  }`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {file.dfAttributeDfFileType} ({humanFile(file.dfAttributeDfFileSize)})
                </a>
              </td>
              <td>
                <div className="df-download-table-bookmark__actions">
                  <Share type="item" group={groupId} item={file.pimId} />
                  <button
                    className="df-icon-btn"
                    title={getTranslation('bookmark_removed_title')}
                    onClick={() => removeItem(file.pimId)}
                  >
                    <svg className="df-action-icon">
                      <use href={getConfig('material_symbols_url') + '#delete_400'} />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
